import React, { useCallback, useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Flyout, FlyoutContent } from "@amzn/stencil-react-components/flyout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { StartDateCardHeader } from "./start-date-header";
import { PreHireAppointmentCardHeader } from "./pre-hire-header";
import { SdaDetails } from "../sda/sda-details";
import { PreHireAppointmentDetails } from "./pre-hire-details";
import { CSSHeader } from "./css-header";

export interface SdaDetails {
  errorCode?: number;
  startDate: string;
  startTime: string;
  publicText: string;
  address: string;
  startDateStatus: string;
}

export interface NheDetails {
  errorCode?: number;
  nheDate: string;
  nheTimeRange: string;
  address: string;
  timezone: string;
}

export interface NheStatus {
  status: string;
}

export enum NHEAppointmentStatusEnum {
  COMPLETED = "COMPLETED",
}

interface AppointmentCardControlledProps {
  sdaDetails?: SdaDetails;
  nheDetails?: NheDetails;
  displayNHE?: boolean;
  scheduleIdFound?: boolean;
  cssURLWithoutScheduleId?: string;
  loading?: boolean;
  error?: boolean;
  eligibleForCSS: boolean;
  onRetry: () => void;
  onViewNHEDetailsClick: () => void;
  onViewStartDateDetailsClick: () => void;
  onViewListOfDocsClick: () => void;
}

export const AppointmentCardControlled = ({
  sdaDetails,
  nheDetails,
  displayNHE,
  scheduleIdFound,
  cssURLWithoutScheduleId,
  onRetry,
  loading,
  error,
  eligibleForCSS,
  onViewNHEDetailsClick,
  onViewStartDateDetailsClick,
  onViewListOfDocsClick,
}: AppointmentCardControlledProps) => {
  const [isStartDateDetailsViewOpen, setIsStartDateDetailsViewOpen] = useState(
    false
  );
  const [isPreHireDetailsViewOpen, setIsPreHireDetailsViewOpen] = useState(
    false
  );

  const openStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(true);
    onViewStartDateDetailsClick();
  }, [isStartDateDetailsViewOpen]);

  const closeStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(false);
  }, [isStartDateDetailsViewOpen]);

  const openPreHireDetailsView = useCallback(() => {
    setIsPreHireDetailsViewOpen(true);
    onViewNHEDetailsClick();
  }, [isPreHireDetailsViewOpen]);

  const closePreHireDetailsView = useCallback(() => {
    setIsPreHireDetailsViewOpen(false);
  }, [isPreHireDetailsViewOpen]);

  if (loading) {
    return (
      <Spinner
        size={SpinnerSize.Small}
        loadingText="Loading..."
        showText={true}
      />
    );
  }

  if (!scheduleIdFound) {
    if (eligibleForCSS) {
      return <CSSHeader cssURLWithoutScheduleId={cssURLWithoutScheduleId} />;
    } else {
      return null;
    }
  }

  if (error) {
    return null; // Remove and customize the error state if needed
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>
          Unable to retrieve the start date details. Please try again later by
          clicking the “Retry” button below.
        </MessageBanner>
        <Row>
          <Button size={ButtonSize.Small} onClick={onRetry}>
            Retry
          </Button>
        </Row>
      </Col>
    );
  }

  if (!sdaDetails || !nheDetails) {
    return null;
  }

  const startDateTime = sdaDetails.startDate + " " + sdaDetails.startTime;
  const vh = window.innerHeight + "px";

  return (
    <>
      {displayNHE && (
        <PreHireAppointmentCardHeader
          nheDate={nheDetails.nheDate}
          onViewDetailsClick={openPreHireDetailsView}
          onViewListOfDocsClick={onViewListOfDocsClick}
        />
      )}
      {!displayNHE && (
        <StartDateCardHeader
          startDate={sdaDetails.startDate}
          startDateStatus={sdaDetails.startDateStatus}
          startTime={sdaDetails.startTime}
          onViewDetailsClick={openStartDateDetailsView}
        />
      )}

      <Flyout
        isOpen={isStartDateDetailsViewOpen}
        close={closeStartDateDetailsView}
      >
        <div style={{ height: vh }}>
          <FlyoutContent
            titleText="First Day Onsite"
            buttons={[
              <Button
                key="close-button"
                variant={ButtonVariant.Primary}
                onClick={closeStartDateDetailsView}
              >
                Close
              </Button>,
            ]}
            onCloseButtonClick={closeStartDateDetailsView}
          >
            <SdaDetails
              startDateTime={startDateTime}
              address={sdaDetails.address}
              publicText={sdaDetails.publicText}
            />
          </FlyoutContent>
        </div>
      </Flyout>

      <Flyout isOpen={isPreHireDetailsViewOpen} close={closePreHireDetailsView}>
        <div style={{ height: vh }}>
          <FlyoutContent
            titleText="Pre-hire Appointment"
            buttons={[
              <Button
                key="close-button"
                variant={ButtonVariant.Primary}
                onClick={closePreHireDetailsView}
              >
                Close
              </Button>,
            ]}
            onCloseButtonClick={closePreHireDetailsView}
          >
            <PreHireAppointmentDetails
              nheDate={nheDetails.nheDate}
              nheTimeRange={nheDetails.nheTimeRange}
              address={nheDetails.address}
            />
          </FlyoutContent>
        </div>
      </Flyout>
    </>
  );
};
